import React, { useEffect, useState } from 'react';
import { adminGetAbandoned, adminDismissAbandoned, adminSendAbandonedCode } from '../../lib/api';
import { toAuWhatsApp } from './adminConstants';
import { useToast } from '../../hooks/use-toast';
import { ShoppingCart, Loader2, Mail, MessageCircle, X, Ticket } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const ago = (s) => {
  const h = Math.round((Date.now() - new Date(s).getTime()) / 36e5);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

export default function AbandonedPanel() {
  const { toast } = useToast();
  const [list, setList] = useState(null);
  const [sending, setSending] = useState('');
  const load = () => adminGetAbandoned().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    await adminDismissAbandoned(id);
    toast({ title: 'Dismissed' });
    load();
  };

  const sendCode = async (a) => {
    setSending(a.id);
    try {
      const res = await adminSendAbandonedCode(a.id);
      toast({ title: `10% code ${res.code} sent to ${a.email}`, description: `Locked to their email · valid until ${res.expiry}` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed to send code', variant: 'destructive' });
    } finally {
      setSending('');
    }
  };

  const mailtoFor = (a) => {
    const subject = encodeURIComponent('Your Australian Luxe Peptides cart is waiting');
    const body = encodeURIComponent(`Hi ${a.fullName || 'there'},\n\nWe noticed you were putting an order together (${a.items || 'your items'}) but didn't quite finish. Your cart is saved and ready whenever you are: https://australianluxe.org/checkout\n\nIf anything held you up — payment, shipping, a question — just reply and we'll sort it.\n\nAustralian Luxe Peptides`);
    return `mailto:${a.email}?subject=${subject}&body=${body}`;
  };

  if (!list) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;

  return (
    <div data-testid="abandoned-panel">
      <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2 mb-1"><ShoppingCart size={20} className="text-alp-gold" /> Abandoned Checkouts</h2>
      <p className="text-alp-gray text-sm mb-6">People who reached checkout in the last 14 days but didn't finish. One tap to follow up.</p>
      {list.length === 0 ? (
        <p className="text-alp-gray text-sm text-center py-14" data-testid="abandoned-empty">Nothing abandoned right now — everyone's checking out. 🎉</p>
      ) : (
        <div className="border border-alp-line rounded-lg divide-y divide-alp-line/50 bg-alp-card/30">
          {list.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`abandoned-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-alp-cream text-sm truncate">{a.fullName || a.email} <span className="text-alp-gray text-xs">· {ago(a.updatedAt)}</span></p>
                <p className="text-alp-gray text-xs truncate">{a.email}{a.phone ? ` · ${a.phone}` : ''} — {a.items || `${a.itemCount} items`}</p>
              </div>
              <span className="text-alp-gold text-sm shrink-0">{money(a.total)}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                {a.recoveryCode ? (
                  <span data-testid={`abandoned-code-sent-${a.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-alp-gold/40 text-alp-gold text-[10px] font-bold uppercase tracking-wider" title={`Sent ${a.recoverySentAt ? ago(a.recoverySentAt) : ''}`}>
                    <Ticket size={11} /> {a.recoveryCode}
                  </span>
                ) : (
                  <button onClick={() => sendCode(a)} disabled={sending === a.id || !a.email} data-testid={`abandoned-code-${a.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded border border-alp-gold/50 text-alp-gold text-xs hover:bg-alp-gold/10 disabled:opacity-50" title="Email them a personal 10% code (7 days, locked to their email)">
                    {sending === a.id ? <Loader2 size={13} className="animate-spin" /> : <Ticket size={13} />} 10% code
                  </button>
                )}
                <a href={mailtoFor(a)} data-testid={`abandoned-email-${a.id}`} className="w-8 h-8 rounded border border-alp-line flex items-center justify-center text-alp-gray hover:text-alp-gold hover:border-alp-gold" title="Email them"><Mail size={14} /></a>
                {a.phone && (
                  <a href={`https://wa.me/${toAuWhatsApp(a.phone)}?text=${encodeURIComponent(`Hi ${a.fullName || ''}! Your ALP cart (${money(a.total)}) is saved and ready whenever you are 🖤`)}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`abandoned-wa-${a.id}`} className="w-8 h-8 rounded border border-emerald-500/40 flex items-center justify-center text-emerald-300 hover:bg-emerald-500/10" title="WhatsApp them"><MessageCircle size={14} /></a>
                )}
                <button onClick={() => dismiss(a.id)} data-testid={`abandoned-dismiss-${a.id}`} className="w-8 h-8 rounded border border-alp-line flex items-center justify-center text-alp-gray hover:text-red-300" title="Dismiss"><X size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
